import React, { useState, useEffect, Fragment } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Row,
  Col,
  Table,
  ProgressBar,
  Alert,
  InputGroup,
  Card
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import ActionButton from 'components/common/ActionButton';
import classNames from 'classnames';
import RawMaterialHelpModal from 'components/pages/haccp/monitoring/common/modal/RawMaterialHelpModal';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { registerLocale } from 'react-datepicker';
import ko from 'date-fns/locale/ko';
registerLocale('ko', ko);

// types
import { RESPONSE_CODE } from 'types/const';
// utils
import comma from 'utils/format/comma';
// api
import { rwmtrCycleList } from 'api/haccp/monitoring/rawMaterialApi';
import {
  excdInfoCount,
  ccpManagementStatus,
  todoInfo
} from 'api/haccp/monitoring/commonApi';
// hooks
import useHistoryState from 'hooks/useHistoryState';

/**
 *  @desciption 모니터링이천 - 원료
 */

const RawMaterial = props => {
  /* router info */
  const history = useHistory();

  /* state */
  const [loading, setLoading] = useState(false);
  const [tab] = useState(props.tab);
  const [pltCd] = useState(props.pltCd); // 공장코드
  const [ctgryGrpCd] = useState(props.ctgryGrpCd); // 분류코드
  const [baseYm, setBaseYm] = useHistoryState(props.baseYm, 'baseYm'); // 조회월
  const [modalShow, setModalShow] = useState(false);

  // 원료주기 모니터링
  const [rwmtrCycle, setRwmtrCycle] = useState([]);
  // 이탈내역조치현황
  const [excdInfo, setCxcdInfo] = useState(null);
  // 한계기준 이탈 현황
  const [insp, setInsp] = useState([]);
  const total = insp?.reduce((acc, data) => acc + data.amount, 0); // bar 그래프 데이터
  const [isInspLimitCheck, setIsInspLimitCheck] = useState(false); // 한계기준 이탈여부
  // CCP관리현황
  const [ccp, setCcp] = useState(null);
  // 원료 - 할일
  const [rwmtrTodo, setRwmtrTodo] = useState([]);
  const [monthCalender, setMonthCalender] = useState([]); // 달력

  /* event */

  // 검사결과 목록페이지 이동
  const onInspectionResult = (
    count,
    month,
    inspTypeSeq,
    inspTypeNm,
    inspTypeCd
  ) => {
    if (count === '' || count === 0) return;

    // 해당연도
    const year = moment(baseYm).year();
    // 원료주기 모니터링에서 선택한 해당월 시작일, 종료일 변환
    const regDtSt = moment([year, month - 1])
      .startOf('month')
      .toDate();
    const regDtEn = moment([year, month - 1])
      .endOf('month')
      .toDate();

    history.push({
      pathname: '/haccp/monitoring/rawMaterial/result',
      state: {
        tab: tab,
        baseYm: baseYm,
        pltCd: pltCd,
        ctgryGrpCd: ctgryGrpCd,
        inspTypeSeq: inspTypeSeq,
        inspTypeNm: inspTypeNm,
        inspTypeCd: inspTypeCd,
        regDtSt: regDtSt,
        regDtEn: regDtEn
      }
    });
  };

  // 한계기준이탈 목록페이지 이동
  const onCriticalLimit = count => {
    // if (count === '' || count === 0) return;
    history.push({
      pathname: '/haccp/monitoring/criticalLimit',
      state: {
        tab: tab,
        pltCd: pltCd,
        ctgryGrpCd: ctgryGrpCd,
        baseYm: baseYm
      }
    });
  };

  // REPORT > 월말결제목록페이지 이동
  const onMonthEndReport = count => {
    if (count === null) return;
    history.push(`/haccp/report/monthend`);
  };

  // CCP관리 목록페이지 이동
  const onCcpManagement = () => {
    history.push({
      pathname: '/haccp/monitoring/rawMaterial/ccpManagement',
      state: {
        tab: tab,
        pltCd: pltCd,
        ctgryGrpCd: ctgryGrpCd,
        baseYm: baseYm
      }
    });
  };

  // 할일 목록페이지 동
  const onTodo = () => {
    history.push({
      pathname: '/haccp/monitoring/rawMaterial/todo',
      state: {
        tab: tab,
        pltCd: pltCd,
        ctgryGrpCd: ctgryGrpCd,
        baseYm: baseYm
      }
    });
  };

  // 조회 API 호출
  const onSearch = async () => {
    const year = Number(moment(baseYm).format('YYYY'));
    const month = Number(moment(baseYm).format('MM'));

    const params = {
      pltCd: pltCd,
      ctgryGrpCd: ctgryGrpCd,
      year: year,
      month: month
    };

    /* 원료주기 모니터링 API 호출 */
    rawMaterialCycle(params);
    /* 이탈내역조치현황 및 한계기준이탈현황 API 호출 */
    departureMeasures(params);
    /* CCP 관리현황 API 호출*/
    ccpManagement(params);
    /* 할일  API 호출*/
    rawMaterialTodo(params);
  };

  /**
   * 원료주기 모니터링 API
   */
  const rawMaterialCycle = async params => {
    setRwmtrCycle([]);
    setLoading(true);
    const response = await rwmtrCycleList(params);

    const { code, list } = response || {};

    if (code !== RESPONSE_CODE.Success) {
      setRwmtrCycle([]);
      setLoading(false);
      return;
    }

    setRwmtrCycle(list);
    setLoading(false);
  };

  /**
   * 이탈내역 조치현황 및 한계기준이탈현황 API
   */
  const departureMeasures = async params => {
    const response = await excdInfoCount(params);
    const { code, data } = response || {};
    if (code !== RESPONSE_CODE.Success) return;

    // 이탈내역 조치현황
    setCxcdInfo(data);
    // 한계기준이탈 알림
    setIsInspLimitCheck(data?.limitExcdCnt > 0 ? true : false);

    // 한계기준 이탈 현황
    setInsp([]);
    setInsp([
      {
        id: 1,
        amount: data?.totalInspResultCnt ?? null,
        variant: 'primary'
      },
      {
        id: 2,
        amount: data?.excdInspResultCnt ?? null,
        variant: 'danger'
      }
    ]);
  };

  /**
   * CCP관리현황 API
   */
  const ccpManagement = async params => {
    const response = await ccpManagementStatus(params);

    const { code, data } = response || {};

    if (code !== RESPONSE_CODE.Success) return;

    // CCP관리 현황
    setCcp({
      ...data,
      ccpInspIrdntNmArr: data.ccpInspIrdntNmArr?.join(','),
      ccpListCount: data?.ccpInspIrdntNmArr?.length ?? 0
    });
  };

  /**
   * 제품 - 할일
   */
  const rawMaterialTodo = async params => {
    const response = await todoInfo(params);

    const { code, list = [] } = response || {};

    if (code !== RESPONSE_CODE.Success) return;

    setRwmtrTodo(list);
  };

  /* method */
  const calendar = month => {
    const ToDay = new Date(month);

    const nowMonth = ToDay.getMonth();

    const [y, m] = getDate(new Date(ToDay.setMonth(nowMonth)));

    const lastDay = getDate(new Date(y, m, 0)).pop() * 1;

    const day = new Date([y, m, 1].join('-')).getDay() * 1;

    const maxDay = Math.ceil((day + lastDay) / 7) * 7;

    let dayList = [];

    for (let i = 1; i <= maxDay; i++) {
      const diff = i - day;
      const d = diff <= lastDay && i > day ? diff : '';

      // 제품할일Data Mapping
      const temp = rwmtrTodo?.find(
        e => Number(moment(new Date(e?.perdAnalsDt)).format('DD')) === d
      );

      const { todoCnt } = temp || {};

      dayList = [
        ...dayList,
        { day: d, count: todoCnt !== undefined ? todoCnt : 0 }
      ];
    }

    const weekList = division(dayList, 7);
    setMonthCalender(weekList);
  };

  const getDate = date => {
    return date.toLocaleDateString().replace(/\./g, '').split(' ');
  };

  const division = (array, n) => {
    const length = array.length;
    const divide =
      Math.floor(length / n) + (Math.floor(length % n) > 0 ? 1 : 0);
    const newArray = [];

    for (let i = 0; i < divide; i++) {
      // 배열 0부터 n개씩 잘라 새 배열에 넣기
      newArray.push({ week: array.splice(0, n) });
    }
    return newArray;
  };

  /* watch */
  useEffect(() => {
    // 조회 API 호출
    onSearch();
  }, [baseYm]);

  useEffect(() => {
    calendar(moment(baseYm).format('YYYY-MM'));
  }, [rwmtrTodo]);

  return (
    <>
      <Row className="ms-0 me-0 mt-3">
        <Col sm={3} className="ps-0">
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text>조회월</InputGroup.Text>
            <div className="flex-fill">
              <DatePicker
                onKeyDown={e => {
                  e.preventDefault();
                }}
                locale="ko"
                dateFormat="yyyy년 MM월"
                className="form-control form-control-sm"
                showMonthYearPicker
                onChange={e => setBaseYm(e)}
                selected={baseYm}
              />
            </div>
          </InputGroup>
        </Col>
        {/* SFSH TODO 미사용 확정시 삭제예정*/}
        {/* {isInspLimitCheck === true && (
          <Col sm={'auto'} className="pe-0 ms-auto">
            <Flex>
              <Alert
                variant="danger cursor-pointer"
                className="fs--1 mb-0 py-2"
                onClick={() => onCriticalLimit(excdInfo?.limitExcdCnt)}
              >
                {moment(baseYm).format('YYYY년MM월')} 검사결과 한계기준 이탈 내역이 있습니다.
              </Alert>
            </Flex>
          </Col>
        )} */}
      </Row>
      <Row className="ms-0 me-0 mt-3">
        {/* 원료주기 모니터링 */}
        <Col sm={9} className="ps-0">
          <Flex justifyContent="between" className="mb-3">
            <h6 className="fs-0 mb-0">원료 주기 모니터링</h6>
            <ActionButton
              icon="question-circle"
              title="도움말"
              variant="action"
              className="p-0 me-1 lh-1"
              onClick={() => setModalShow(true)}
            />
          </Flex>
          <div className="scrollbar" style={{ maxHeight: '275px' }}>
            <Table responsive bordered className="fs--1 text-center mb-0">
              <thead className="bg-200 text-700 align-middle border-300">
                <tr>
                  <th className="text-nowrap"></th>
                  <th className="text-nowrap">1월</th>
                  <th className="text-nowrap">2월</th>
                  <th className="text-nowrap">3월</th>
                  <th className="text-nowrap">4월</th>
                  <th className="text-nowrap">5월</th>
                  <th className="text-nowrap">6월</th>
                  <th className="text-nowrap">7월</th>
                  <th className="text-nowrap">8월</th>
                  <th className="text-nowrap">9월</th>
                  <th className="text-nowrap">10월</th>
                  <th className="text-nowrap">11월</th>
                  <th className="text-nowrap">12월</th>
                </tr>
              </thead>
              <tbody className="align-middle">
                {rwmtrCycle?.map(
                  ({ inspTypeSeq, inspTypeCd, inspTypeNm, cntArr }, index) => (
                    <tr key={index}>
                      <td className="bg-200 border-300 border-bottom">
                        {inspTypeNm || '-'}
                      </td>
                      {cntArr?.map((e, index) => (
                        <td
                          key={index}
                          className={e !== null ? 'cursor-pointer' : ''}
                          onClick={() =>
                            onInspectionResult(
                              e,
                              index + 1,
                              inspTypeSeq,
                              inspTypeNm,
                              inspTypeCd
                            )
                          }
                        >
                          {e === '' ? '-' : e}
                        </td>
                      ))}
                    </tr>
                  )
                )}
                {loading && (
                  <tr>
                    <td colSpan={13} className="w-auto">
                      데이터 조회중입니다....
                    </td>
                  </tr>
                )}
                {rwmtrCycle.length < 1 && loading === false && (
                  <tr>
                    <td colSpan={13} className="w-auto">
                      원료 주기 모니터링 데이터가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Col>
        {/* 이탈내역 조치현황 */}
        <Col sm={3} className="pe-0">
          <h6 className="fs-0 mb-3">이탈 내역 조치 현황</h6>
          <Table
            bordered
            className="fs--1 text-center mb-0 h-100"
            style={{ maxHeight: 'calc(100% - 2.233rem)' }}
          >
            <tbody className="align-middle">
              <tr>
                <th className="bg-200 text-700 border-300 w-75 border-y">
                  한계 기준 이탈 수
                </th>
                <td
                  className="text-danger fw-bold cursor-pointer"
                  onClick={() => onCriticalLimit(excdInfo?.excdTotalCnt)}
                >
                  {excdInfo?.excdTotalCnt || '-'}
                </td>
              </tr>
              <tr>
                <th className="bg-200 text-700 border-300 w-75 border-y">
                  조치내역 기안 미등록 수
                </th>
                <td
                  className="fw-bold cursor-pointer"
                  onClick={() => onCriticalLimit(excdInfo?.limitExcdCnt)}
                >
                  {excdInfo?.limitExcdCnt || '-'}
                </td>
              </tr>
              <tr>
                <th className="bg-200 text-700 border-300 w-75 border-y">
                  조치내역 기안 등록 및 결재 요청 수
                </th>
                <td
                  className="fw-bold cursor-pointer"
                  onClick={() => onCriticalLimit(excdInfo?.excdCommentRegCnt)}
                >
                  {excdInfo?.excdCommentRegCnt || '-'}
                </td>
              </tr>
              <tr>
                <th className="bg-200 text-700 border-300 w-75 border-y">
                  조치내역 기안 결재 수
                </th>
                <td
                  className="fw-bold cursor-pointer"
                  onClick={() => onCriticalLimit(excdInfo?.excdApprovalCnt)}
                >
                  {excdInfo?.excdApprovalCnt || '-'}
                </td>
              </tr>
              <tr>
                <th className="bg-200 text-700 border-300 w-75 border-y">
                  월말 보고 결재 수 / 전체 월말보고 생성 수
                </th>
                <td
                  className="fw-bold cursor-pointer"
                  onClick={() => onMonthEndReport(excdInfo?.monthApprovalCnt)}
                >
                  {excdInfo?.monthApprovalCnt || '-'} /{' '}
                  {excdInfo?.monthEndReportCnt || '-'}
                </td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>
      <Row className="ms-0 me-0 mt-3">
        <Col sm={6} className="ps-0">
          {/* 한계기준 이탈 현황 */}
          <Col sm={12} className="p-0">
            <Flex justifyContent="between" className="mb-4">
              <h6 className="fs-0 mb-3">한계기준 이탈 현황</h6>
              <p
                className="p-0 lh-1 mb-3 cursor-pointer text-primary"
                onClick={() => onCriticalLimit(excdInfo?.complet1)}
              >
                {'자세히 보기 >'}
              </p>
            </Flex>
            <ProgressBar
              className="fs--1 rounded-3 overflow-visible mb-4"
              style={{ height: 20 }}
            >
              {insp?.map((item, index) => (
                <ProgressBar
                  variant={item.variant}
                  now={(item.amount / total) * 100}
                  key={item.id}
                  className={classNames('overflow-visible position-relative', {
                    'rounded-end rounded-pill': index === 0,
                    'rounded-start rounded-pill': index === insp.length - 1,
                    ' border-end border-white border-2':
                      index !== insp.length - 1
                  })}
                  label={
                    <span
                      className="text-700 position-absolute translate-sm-middle-x"
                      style={{ top: -27, left: '50%' }}
                    >
                      {item.amount !== 0 && (
                        <strong className="fw-bold">{item.amount}</strong>
                      )}
                    </span>
                  }
                />
              ))}
            </ProgressBar>
            <Table
              bordered
              className="fs--1 text-center mb-0 h-100"
              style={{ maxHeight: 'calc(100% - 20px - 5.8rem)' }}
            >
              <thead className="bg-200 text-700 align-middle border-300">
                <tr>
                  <th className="text-nowrap">
                    <FontAwesomeIcon
                      icon="circle"
                      className="text-primary me-1"
                    />
                    검사결과
                  </th>
                  <th className="text-nowrap">
                    <FontAwesomeIcon
                      icon="circle"
                      className="text-danger me-1"
                    />
                    한계기준 이탈
                  </th>
                </tr>
              </thead>
              <tbody className="align-middle">
                <tr>
                  <td className="border-300 fw-bold text-primary">
                    {comma(insp[0]?.amount) || '-'}
                  </td>
                  <td className="border-300 fw-bold text-danger">
                    {comma(insp[1]?.amount) || '-'}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Col>
          {/* CCP 관리 현황 */}
          <Col sm={12} className="p-0 mt-3">
            <Flex justifyContent="between" className="mb-3">
              <h6 className="fs-0 mb-0">CCP 관리 현황</h6>
              <p
                className="p-0 lh-1 mb-3 cursor-pointer text-primary"
                onClick={() => onCcpManagement()}
              >
                {'자세히 보기 >'}
              </p>
            </Flex>
            <Card className="mb-3 shadow-none">
              <Card.Body className="bg-200 fs--1 py-2 rounded-3">
                <p className="mb-3">
                  CCP 관리 항목 ({ccp?.ccpListCount || 0}개)
                </p>
                <p className="mb-0">{ccp?.ccpInspIrdntNmArr || '-'}</p>
              </Card.Body>
            </Card>
            <Table bordered className="fs--1 text-center mb-0">
              <tbody className="align-middle">
                <tr>
                  <th className="bg-200 text-700 border-300 w-50 border-y">
                    CCP 포함된 검사결과
                  </th>
                  <td>{comma(ccp?.totalCcpInspResultCnt) || '-'}</td>
                </tr>
                <tr>
                  <th className="bg-200 text-700 border-300 w-50 border-y">
                    CCP 포함 한계기준 이탈
                  </th>
                  <td className="text-danger">
                    {comma(ccp?.excdCcpInspResultCnt) || '-'}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Col>
        </Col>
        {/* 할일 */}
        <Col sm={6} className="ps-0">
          <Flex justifyContent="between" className="mb-3">
            <h6 className="fs-0 mb-0">할 일</h6>
            <p
              className="p-0 lh-1 mb-3 cursor-pointer text-primary"
              onClick={() => onTodo()}
            >
              {'자세히 보기 >'}
            </p>
          </Flex>
          <Table bordered className="fs--1 text-center mb-0">
            <thead className="bg-200 text-700 align-middle border-300">
              <tr>
                <th className="fs-0 fw-bold" colSpan={7}>
                  {moment(baseYm).format('YYYY')}년{' '}
                  {moment(baseYm).format('MM')}월
                </th>
              </tr>
              <tr>
                <th className="py-1" style={{ width: 'calc(100% / 7)' }}>
                  일
                </th>
                <th className="py-1" style={{ width: 'calc(100% / 7)' }}>
                  월
                </th>
                <th className="py-1" style={{ width: 'calc(100% / 7)' }}>
                  화
                </th>
                <th className="py-1" style={{ width: 'calc(100% / 7)' }}>
                  수
                </th>
                <th className="py-1" style={{ width: 'calc(100% / 7)' }}>
                  목
                </th>
                <th className="py-1" style={{ width: 'calc(100% / 7)' }}>
                  금
                </th>
                <th className="py-1" style={{ width: 'calc(100% / 7)' }}>
                  토
                </th>
              </tr>
            </thead>
            <tbody className="text-start align-top">
              {monthCalender?.map(({ week }, index) => (
                <tr key={index}>
                  {week?.map(({ day, count }, index) => (
                    <Fragment key={index}>
                      <td className="py-1" style={{ height: '4rem' }}>
                        {day}
                        {count !== 0 && day !== '' && (
                          <p className="text-danger fs--2">
                            정기분석일: {count}건
                          </p>
                        )}
                      </td>
                    </Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
      {modalShow === true && (
        <RawMaterialHelpModal
          modalShow={modalShow}
          setModalShow={setModalShow}
        ></RawMaterialHelpModal>
      )}
    </>
  );
};

export default RawMaterial;
